import type { Appointment } from '@medplum/fhirtypes';
import {
  Alert,
  Box,
  CheckIcon,
  Chip,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import {
  IconBuilding,
  IconClock,
} from '@tabler/icons-react';
import { useEffect, useMemo } from 'react';
import {
  noteMetadataSnapshotFormSchema,
  noteMetadataSnapshotSchema,
  type NoteMetadataBillingCode,
  type NoteMetadataSnapshot,
} from '@/schemas/note-metadata.schema';
import { formatShortDate } from '../utils/dateFormatters';
import { APPOINTMENT_MODALITY_OPTIONS } from '../utils/appointmentModality';
import {
  ParticipantAttendanceSection,
  type ParticipantAttendanceRow,
} from './ParticipantAttendanceSection';
import type { ParticipantAttendanceFhirStatus } from '../utils/participantAttendance';
import {
  formatSessionTimingMismatchLabels,
  type EncounterSessionTiming,
} from '@/lib/encounterSessionTiming';
import type { AppointmentSessionType } from '@/pages/calendar/appointmentTypeCatalog';
import { formatBillingRowLabel } from '../noteMetadataMapping';
import {
  AppointmentServiceCodesTable,
  type AppointmentServiceCodeRow,
} from '@/components/AppointmentServiceCodesTable';
import { EncounterScrubberMetadataFields } from './EncounterScrubberMetadataFields';
import {
  InlineFieldWarningIcon,
  INLINE_FIELD_WARNING_SECTION_WIDTH,
  inlineFieldWarningInputStyles,
} from '@/components/InlineFieldWarningIcon';

export interface NoteMetadataSectionProps {
  /** Live Appointment for static header fields; optional in read-only view (falls back to metadata). */
  appointment?: Appointment;
  metadata: NoteMetadataSnapshot;
  onChange: (metadata: NoteMetadataSnapshot) => void;
  readOnly?: boolean;
  /** Zoom-derived four-duration timing from Encounter (non-blocking review). */
  sessionTiming?: EncounterSessionTiming;
  /** Configured clinical session types (HealthcareService catalog). */
  serviceTypeOptions?: AppointmentSessionType[];
  /** Per-patient attendance (preferred over appointment-level attendance field). */
  participantAttendanceRows?: ParticipantAttendanceRow[];
  participantAttendanceByPatientId?: Record<string, ParticipantAttendanceFhirStatus | undefined>;
  /** Baseline per-participant attendance when the editor opened (transition rules). */
  savedParticipantAttendanceByPatientId?: Record<string, ParticipantAttendanceFhirStatus | undefined>;
  participantLocationByPatientId?: Record<string, string | undefined>;
  participantLocationOptionsByPatientId?: Record<string, string[]>;
  participantLocationErrorsByPatientId?: Record<string, string>;
  onParticipantAttendanceChange?: (patientId: string, status: ParticipantAttendanceFhirStatus) => void;
  onParticipantLocationChange?: (patientId: string, location: string | undefined) => void;
  /** Cohort shared notes: unresolved attendance blocks sign/finalize. */
  requireResolvedAttendance?: boolean;
  /** Blocking billing-evaluation validation (shown under those fields). */
  scrubberValidationErrors?: Record<string, string>;
  /** Non-blocking CPT/duration hints (tooltip icon on session duration and CPT rows). */
  inlineFieldWarnings?: Record<string, string>;
  /** When true, omit participant attendance (edited in a separate modal). */
  hideParticipantAttendance?: boolean;
  /** Practice site / room labels for session-level location chips. */
  sessionLocationOptions?: string[];
}

function formatAppointmentStartTime(start: string | undefined): string {
  if (!start) return '—';
  try {
    return new Date(start).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return start;
  }
}

function ensureBillingRows(metadata: NoteMetadataSnapshot): NoteMetadataBillingCode[] {
  const rows = metadata.billingCodes ?? [];
  if (rows.length === 0) {
    return [{ code: 'unspecified', units: 1 }];
  }
  return rows.map((row) => ({ ...row, units: row.units ?? 1 }));
}

export function NoteMetadataSection({
  appointment,
  metadata,
  onChange,
  readOnly = false,
  serviceTypeOptions = [],
  participantAttendanceRows = [],
  participantAttendanceByPatientId = {},
  savedParticipantAttendanceByPatientId = {},
  participantLocationByPatientId = {},
  participantLocationOptionsByPatientId = {},
  participantLocationErrorsByPatientId = {},
  onParticipantAttendanceChange,
  onParticipantLocationChange,
  requireResolvedAttendance = false,
  sessionTiming,
  scrubberValidationErrors = {},
  inlineFieldWarnings = {},
  hideParticipantAttendance = false,
  sessionLocationOptions = [],
}: NoteMetadataSectionProps): JSX.Element {
  const form = useForm<NoteMetadataSnapshot>({
    initialValues: metadata,
    validate: zod4Resolver(noteMetadataSnapshotFormSchema),
    validateInputOnBlur: true,
  });

  const metadataSyncKey = useMemo(() => JSON.stringify(metadata), [metadata]);

  useEffect(() => {
    form.setValues(metadata);
    form.resetDirty(metadata);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when snapshot content changes, not object identity
  }, [metadataSyncKey]);

  const commitValues = (values: NoteMetadataSnapshot) => {
    const parsed = noteMetadataSnapshotSchema.safeParse(values);
    if (parsed.success) {
      onChange(parsed.data);
    }
  };

  const staticStart = appointment?.start ?? metadata.start;
  const sessionDateLabel = staticStart ? formatShortDate(staticStart) : '—';
  const sessionStartTimeLabel = formatAppointmentStartTime(staticStart);
  const sessionDurationLabel =
    form.values.durationMinutes != null ? `${form.values.durationMinutes} min` : '—';

  const billingRows = ensureBillingRows(form.values);

  /** Snapshot billing rows mapped to the shared service-codes table shape. */
  const serviceCodeRows: AppointmentServiceCodeRow[] = billingRows.map((row, index) => {
    const serviceType = serviceTypeOptions.find((st) => st.id === row.healthcareServiceId);
    const code =
      serviceType?.cptCode?.trim()
      || (row.code && row.code !== 'unspecified' ? row.code : '—');
    return {
      id: row.chargeItemId ?? `${row.code}-${index}`,
      code,
      description: serviceType?.billingTitle ?? row.display ?? '',
      units: row.units ?? 1,
    };
  });

  const billingWarningByRowIndex: Record<number, string> = {};
  billingRows.forEach((_, index) => {
    const warning = inlineFieldWarnings[`billingRow-${index}`] ?? inlineFieldWarnings.billingCodes;
    if (warning) billingWarningByRowIndex[index] = warning;
  });

  const participantAttendanceBlock =
    !hideParticipantAttendance && participantAttendanceRows.length > 0 ? (
      <ParticipantAttendanceSection
        rows={participantAttendanceRows}
        valueByPatientId={participantAttendanceByPatientId}
        savedStatusByPatientId={savedParticipantAttendanceByPatientId}
        locationByPatientId={participantLocationByPatientId}
        locationOptionsByPatientId={participantLocationOptionsByPatientId}
        locationErrorsByPatientId={participantLocationErrorsByPatientId}
        onChange={onParticipantAttendanceChange ?? (() => {})}
        onLocationChange={onParticipantLocationChange}
        readOnly={readOnly || !onParticipantAttendanceChange}
        requireResolvedAttendance={requireResolvedAttendance}
      />
    ) : null;

  const selectedLocations = form.values.locations ?? [];
  const locationChipOptions = useMemo(() => {
    const set = new Set<string>([...sessionLocationOptions, ...selectedLocations]);
    return [...set];
  }, [sessionLocationOptions, selectedLocations]);

  const locationChips =
    locationChipOptions.length > 0 ? (
      <Stack gap={6}>
        <Text size="sm" fw={600}>
          Location(s)
        </Text>
        <Chip.Group
          multiple
          value={selectedLocations}
          onChange={(next) => {
            if (readOnly) return;
            const locations = next.filter(Boolean);
            const updated = { ...form.values, locations };
            form.setFieldValue('locations', locations);
            commitValues(updated);
          }}
        >
          <Group gap="xs">
            {locationChipOptions.map((label) => (
              <Chip key={label} value={label} variant="outline" color="gold" icon={<CheckIcon size={12} />} disabled={readOnly}>
                {label}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
      </Stack>
    ) : null;

  const timingMismatchLabels =
    sessionTiming && sessionTiming.mismatches.length > 0
      ? formatSessionTimingMismatchLabels(sessionTiming)
      : [];

  const timingBanner =
    timingMismatchLabels.length > 0 ? (
      <Alert color="yellow" variant="light" mb="sm" title="Session timing review">
        <Stack gap={4}>
          {timingMismatchLabels.map((line) => (
            <Text key={line} size="sm">
              {line}
            </Text>
          ))}
          {sessionTiming?.actualSessionMinutes != null ? (
            <Text size="xs" c="gray.6">
              Documented session: {sessionTiming.actualSessionMinutes} min
              {sessionTiming.serviceTypeExpectedMinutes != null
                ? ` · Service default: ${sessionTiming.serviceTypeExpectedMinutes} min`
                : ''}
            </Text>
          ) : null}
        </Stack>
      </Alert>
    ) : null;

  const staticFieldsGrid = (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm" mb="md">
      <MetadataReadOnlyRow label="Date" value={sessionDateLabel} />
      <MetadataReadOnlyRow label="Start time" value={sessionStartTimeLabel} />
    </SimpleGrid>
  );

  const modalityDurationGrid = readOnly ? (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
      <MetadataReadOnlyRow label="Modality" value={metadata.modality ?? '—'} />
      <MetadataReadOnlyRow label="Session duration" value={sessionDurationLabel} />
    </SimpleGrid>
  ) : (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
      <Select
        label="Modality"
        value={form.values.modality ?? null}
        error={form.errors.modality}
        onChange={(val) => {
          const next = {
            ...form.values,
            modality: (val as NoteMetadataSnapshot['modality']) ?? undefined,
          };
          form.setFieldValue('modality', next.modality);
          commitValues(next);
        }}
        data={APPOINTMENT_MODALITY_OPTIONS}
        clearable
        leftSection={<IconBuilding size={16} />}
      />
      <NumberInput
        label="Session duration"
        data-note-field="durationMinutes"
        min={0}
        max={480}
        clampBehavior="strict"
        decimalScale={1}
        step={0.1}
        value={form.values.durationMinutes ?? undefined}
        error={form.errors.durationMinutes}
        onChange={(val) => {
          const durationMinutes = typeof val === 'number' ? val : undefined;
          form.setFieldValue('durationMinutes', durationMinutes);
          commitValues({ ...form.values, durationMinutes });
        }}
        leftSection={<IconClock size={16} />}
        suffix="min"
        rightSection={
          inlineFieldWarnings.durationMinutes ? (
            <InlineFieldWarningIcon message={inlineFieldWarnings.durationMinutes} />
          ) : undefined
        }
        rightSectionWidth={
          inlineFieldWarnings.durationMinutes ? INLINE_FIELD_WARNING_SECTION_WIDTH : undefined
        }
        styles={inlineFieldWarningInputStyles(Boolean(inlineFieldWarnings.durationMinutes))}
      />
    </SimpleGrid>
  );

  const billingRowsTable = (
    <Stack gap="xs">
      <Text size="sm" fw={600}>
        Service codes
      </Text>
      <AppointmentServiceCodesTable
        rows={serviceCodeRows}
        warningByRowIndex={billingWarningByRowIndex}
        withNoteFieldAnchors
      />
    </Stack>
  );

  if (!appointment && !readOnly) {
    return (
      <Text size="sm" c="dimmed">
        Appointment details are loading…
      </Text>
    );
  }

  if (readOnly) {
    const billingLabel =
      billingRows
        .map((row) => {
          const serviceType = serviceTypeOptions.find((st) => st.id === row.healthcareServiceId);
          return formatBillingRowLabel(row, serviceType);
        })
        .join('; ') || '—';

    return (
      <Box>
        {!hideParticipantAttendance ? (
          <Title order={4} mb="sm">
            Session metadata
          </Title>
        ) : null}
        {timingBanner}
        {staticFieldsGrid}
        {modalityDurationGrid}
        {locationChips && <Box mt="md">{locationChips}</Box>}
        {participantAttendanceBlock && <Box mt="md">{participantAttendanceBlock}</Box>}
        <Box mt="md">
          <MetadataReadOnlyRow label="Service codes" value={billingLabel} />
        </Box>
        <EncounterScrubberMetadataFields
          metadata={metadata}
          onChange={onChange}
          readOnly
          serviceTypeOptions={serviceTypeOptions}
        />
      </Box>
    );
  }

  return (
    <Box>
      {!hideParticipantAttendance ? (
        <>
          <Title order={4} mb="sm">
            Session metadata
          </Title>
          <Text size="sm" c="dimmed" mb="md">
            Date and start time come from the appointment, and service codes from the appointment
            service type. Edits to modality and session duration autosave with the note and are
            checked before signing.
          </Text>
        </>
      ) : null}
      {timingBanner}
      {staticFieldsGrid}
      {modalityDurationGrid}
      {locationChips && <Box mt="md">{locationChips}</Box>}
      {participantAttendanceBlock && <Box mt="md">{participantAttendanceBlock}</Box>}
      <Box mt="md">{billingRowsTable}</Box>
      <EncounterScrubberMetadataFields
        metadata={form.values}
        onChange={(next) => {
          form.setValues(next);
          commitValues(next);
        }}
        serviceTypeOptions={serviceTypeOptions}
        validationErrors={scrubberValidationErrors}
      />
    </Box>
  );
}

function MetadataReadOnlyRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Stack
      gap={2}
      p="xs"
      style={{
        borderRadius: 'var(--mantine-radius-sm)',
        backgroundColor: 'var(--mantine-color-gray-0)',
        border: '1px solid var(--mantine-color-gray-2)',
      }}
    >
      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
        {label}
      </Text>
      <Text size="sm" c="gray.8">
        {value}
      </Text>
    </Stack>
  );
}
